import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Magnetic } from "@/components/motion";
import { ContractorSignupForm } from "@/pages/Landing";
import SiteHeader from "@/components/SiteHeader";

function ProAuthShell({ children, brandTitle, brandSub, sideCopy }) {
  return (
    <div className="min-h-screen bg-[#0D1421] text-[#EEEEEF] flex flex-col">
      <SiteHeader />
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex bg-[#010204] text-[#EEEEEF] p-14 items-center grain relative overflow-hidden">
          <div className="max-w-md relative z-10">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
              — {brandSub}
            </div>
            <h1 className="font-editorial text-5xl xl:text-6xl font-light tracking-tighter leading-[0.9] break-word-safe">
              {brandTitle}
            </h1>
            <p className="mt-8 text-[#C9C6C6] leading-relaxed">{sideCopy}</p>
          </div>
        </div>
        <div className="flex items-center justify-center p-5 sm:p-8 md:p-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-xl"
          >
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 *  Pro Signup — dedicated page
 * ============================================================ */
export function ProSignup() {
  return (
    <ProAuthShell
      brandTitle={<>Join the network. <span className="italic">Get paid.</span></>}
      brandSub="Pro signup"
      sideCopy="Set up your business profile, choose your service area, and pick a monthly plan. No pay-per-lead. Cancel any time."
    >
      <div
        className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-4"
        data-testid="pro-signup-page"
      >
        — Create your pro account
      </div>
      <h2 className="font-editorial text-3xl sm:text-4xl md:text-5xl font-light tracking-tighter leading-[0.95] break-word-safe">
        Tell us about your business.
      </h2>
      <p className="mt-3 text-[#C9C6C6] text-sm sm:text-base leading-relaxed">
        Takes ~2 minutes. After you save, you&apos;ll pick a plan and start
        seeing local leads.
      </p>

      <div className="mt-8">
        <ContractorSignupForm scope="page" />
      </div>

      <p className="mt-6 text-sm text-[#C9C6C6]">
        Already a pro?{" "}
        <Link
          to="/pro/login"
          className="text-[#0E93EC] underline underline-offset-4"
          data-testid="pro-signup-to-login"
        >
          Log in
        </Link>
      </p>
    </ProAuthShell>
  );
}

/* ============================================================
 *  Pro Login — Google OAuth (Emergent-managed)
 * ============================================================ */
export function ProLogin() {
  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/workspace";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };
  return (
    <ProAuthShell
      brandTitle={<>Your leads. <br /><span className="italic">One inbox.</span></>}
      brandSub="Pro Workspace"
      sideCopy="Every local job routed straight to your dashboard. Move a card from New to Won in one click. Notes, status, phone, all in one place."
    >
      <div
        className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-4"
        data-testid="pro-login-page"
      >
        — Pro login
      </div>
      <h2 className="font-editorial text-4xl sm:text-5xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95] break-word-safe">
        Welcome back.
      </h2>
      <p className="mt-6 text-[#C9C6C6] leading-relaxed">
        Sign in to see your leads inbox, move deals through the pipeline, and
        manage your subscription.
      </p>
      <Magnetic strength={6}>
        <Button
          onClick={startGoogleLogin}
          className="group mt-10 w-full bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-6 pr-5 text-base font-medium"
          data-testid="google-signin-btn"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 mr-3 shrink-0" aria-hidden="true">
            <path fill="#fff" d="M12 11v2.8h4.6c-.2 1.2-1.3 3.5-4.6 3.5-2.8 0-5-2.3-5-5.1s2.2-5.1 5-5.1c1.6 0 2.7.7 3.3 1.3l2.2-2.1C15.9 5 14.1 4 12 4 7.6 4 4 7.6 4 12s3.6 8 8 8c4.6 0 7.7-3.2 7.7-7.8 0-.5-.1-.9-.1-1.2H12z" />
          </svg>
          <span className="flex-1 text-left">Continue with Google</span>
          <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
            <ArrowRight className="w-4 h-4" />
          </span>
        </Button>
      </Magnetic>
      <p className="mt-8 text-sm text-[#C9C6C6]">
        New here?{" "}
        <Link
          to="/pro/signup"
          className="text-[#0E93EC] underline underline-offset-4"
          data-testid="pro-login-to-signup"
        >
          Create your pro account
        </Link>
      </p>
    </ProAuthShell>
  );
}
